import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router'
import { useState } from 'react'

import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import { Dialog } from '@/components/ui/Dialog'
import { TrashIcon } from '@/components/ui/icons'
import { loungeQueryOptions } from '@/features/lounges/api'
import { useDeleteNook, useNook, useRenameNook, useSetNookTopic } from '@/features/nooks/api'
import { errorMessage } from '@/lib/graphql'

/**
 * One nook's settings — the one door to everything about a room.
 *
 * **A ratified surface-pattern change.** Renaming used to happen inline in the
 * sidebar and the topic was edited in the nook header, which meant one room's
 * settings lived in two places and neither of them looked like a place. Both are
 * here now, behind the gear on the row.
 *
 * The nook id is in the path rather than nested under the open nook, because the
 * gear can be clicked on a row that is not the one you are reading.
 */
export const Route = createFileRoute('/_app/l/$loungeSlug/nooks/$nookId/settings')({
  beforeLoad: async ({ context: { queryClient }, params: { loungeSlug } }) => {
    const { lounge } = await queryClient.ensureQueryData(loungeQueryOptions(loungeSlug))

    // Members cannot shape the rooms, so they do not get the door — the gear is
    // hidden for them too, but a typed URL must not be a way around it.
    if (lounge?.myRole !== 'OWNER' && lounge?.myRole !== 'MOD') {
      throw redirect({ to: '/l/$loungeSlug', params: { loungeSlug } })
    }
  },
  component: NookSettings,
})

/**
 * Waits for the nook before drawing the form.
 *
 * The split is load-bearing, not tidiness. The form seeds two `useState`s from
 * the nook's current name and topic, and `useState` only reads its argument on
 * the *first* render — so with the query inlined here, opening this route
 * cold (a typed URL, a reload, a deep link) initialised both boxes from an
 * `undefined` nook and left them empty for good once the data landed. The
 * fields looked merely blank; what they actually were was a rename to the empty
 * string, armed, with `Save changes` enabled because "" differs from the real
 * name.
 *
 * Rendering the form only once there is something to seed it from is what makes
 * that unrepresentable, and the `key` covers the other half: the gear can be
 * clicked on a different row while this route is already open, and without it
 * React would reuse the mounted form and keep the previous room's text.
 */
function NookSettings() {
  const { nookId } = Route.useParams()
  const { data } = useNook(nookId)

  if (!data?.nook) return null

  return <NookSettingsForm key={data.nook.id} nook={data.nook} />
}

/** The nook as `useNook` hands it over, once it has one. */
type LoadedNook = NonNullable<ReturnType<typeof useNook>['data']>['nook']

function NookSettingsForm({ nook }: { nook: LoadedNook }) {
  const { loungeSlug } = Route.useParams()
  const nookId = nook.id
  const navigate = useNavigate()
  const rename = useRenameNook()
  const setTopic = useSetNookTopic()
  const remove = useDeleteNook(nook.loungeId)

  const [name, setName] = useState(nook.name)
  const [topic, setTopic_] = useState(nook.topic ?? '')
  const [confirmingDelete, setConfirmingDelete] = useState(false)

  const close = () => void navigate({ to: '/l/$loungeSlug', params: { loungeSlug } })

  const nameChanged = name.trim() !== nook.name
  const topicChanged = topic.trim() !== (nook.topic ?? '')
  const pending = rename.isPending || setTopic.isPending
  // A room with no name is not a thing you can mean to make. The server refuses
  // it too; disabling the button is what stops the refusal being the first you
  // hear of it. A blank *topic* is fine and clears it — hence only the name.
  const dirty = (nameChanged || topicChanged) && name.trim().length > 0

  /**
   * Save, and get out of the way.
   *
   * A settings dialog that stays open after a successful save is asking "did
   * that work?" — the sidebar row has already renamed itself behind the scrim,
   * and the only thing left to do here is leave. Failure keeps it open, because
   * then there really is something to read.
   *
   * Two mutations rather than one, because the API has two — and both are
   * announced on the same `NookUpdated` event, so the sidebar row settles once
   * either way. `Promise.all` over only the ones that actually changed is what
   * lets the close wait for *both* without inventing a third mutation: closing
   * on the first to land would dismiss the dialog while the second could still
   * fail with nowhere to say so.
   */
  const save = () => {
    const pendingWrites: Promise<unknown>[] = []

    if (nameChanged) pendingWrites.push(rename.mutateAsync({ nookId, name: name.trim() }))
    if (topicChanged) {
      const trimmed = topic.trim()
      pendingWrites.push(
        setTopic.mutateAsync({ nookId, topic: trimmed.length > 0 ? trimmed : null }),
      )
    }

    // The rejection is already on `rename.error` / `setTopic.error`, which the
    // form errors below render; this catch only stops it becoming unhandled.
    void Promise.all(pendingWrites)
      .then(close)
      .catch(() => undefined)
  }

  return (
    <>
      <Dialog open={!confirmingDelete} onClose={close} title="Nook settings" narrow>
        <div className="dialog-body">
          <div className="field">
            <label className="field-caps" htmlFor="nook-name">
              Name
            </label>
            <input
              id="nook-name"
              className="field-input field-input-raised"
              value={name}
              onChange={(event) => setName(event.target.value)}
            />
          </div>

          <div className="field">
            <label className="field-caps" htmlFor="nook-topic">
              Topic
            </label>
            <input
              id="nook-topic"
              className="field-input field-input-raised"
              value={topic}
              onChange={(event) => setTopic_(event.target.value)}
              placeholder="What is this room for?"
            />
          </div>

          {rename.isError && (
            <p className="form-error" role="alert">
              {errorMessage(rename.error, 'That name could not be saved.')}
            </p>
          )}
          {setTopic.isError && (
            <p className="form-error" role="alert">
              {errorMessage(setTopic.error, 'That topic could not be saved.')}
            </p>
          )}
          {remove.isError && (
            <p className="form-error" role="alert">
              {errorMessage(remove.error, 'That nook could not be deleted.')}
            </p>
          )}
        </div>

        {/* The mock's shape: one dark full-width primary, and delete as a small
         * red icon button beside it — deliberately not a text button, so the
         * destructive act never reads as the second-most-obvious thing to do. */}
        <div className="dialog-actions dialog-actions-split">
          <button
            type="button"
            className="btn btn-primary btn-block"
            disabled={pending || !dirty}
            onClick={save}
          >
            {pending ? 'Saving…' : 'Save changes'}
          </button>

          <button
            type="button"
            className="icon-button icon-button-danger"
            onClick={() => setConfirmingDelete(true)}
            aria-label={`Delete ${nook.name}`}
            title="Delete this nook"
          >
            <TrashIcon />
          </button>
        </div>
      </Dialog>

      {/* Two steps, still: the icon opens this, it never deletes directly.
       * Replaces the settings dialog rather than stacking on it — one modal at a
       * time, never two scrims. */}
      <ConfirmDialog
        open={confirmingDelete}
        onCancel={() => setConfirmingDelete(false)}
        onConfirm={() => remove.mutate(nookId, { onSuccess: close })}
        pending={remove.isPending}
        title={`Delete ${nook.name}?`}
        description="Everything said in this nook goes with it. This cannot be undone."
        confirmLabel="Delete"
      />
    </>
  )
}
